import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "./TermsConditions.css"; // reuse base styles
import "./Services.css"; // additional service‑specific styles

// ---------- SVG placeholder generator (if images are missing) ----------
const svgPlaceholder = (
  width,
  height,
  text,
  bgColor = "#e9ecef",
  textColor = "#6c757d",
) => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <rect width="${width}" height="${height}" fill="${bgColor}" />
      <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="${Math.min(width, height) / 5}" fill="${textColor}" text-anchor="middle" dy=".3em">${text}</text>
    </svg>
  `;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const Services = () => {
  // Service data array – each service now only needs icon, title, shortDesc
  const services = [
    {
      id: 1,
      title: "AEPS (Aadhaar Enabled Payment System)",
      icon: "fingerprint-icon.png",
      shortDesc:
        "Enable cash withdrawals, balance inquiries, and mini statements using Aadhaar authentication.",
    },
    {
      id: 2,
      title: "DMT (Domestic Money Transfer)",
      icon: "bank-icon.png",
      shortDesc: "Instant, secure money transfer to any bank account in India.",
    },
    {
      id: 3,
      title: "CMS (Cash Management Services)",
      icon: "rupee-icon.png",
      shortDesc: "Cash collection and settlement solutions for businesses.",
    },
    {
      id: 4,
      title: "PAN Card Services",
      icon: "pancard-icon.png",
      shortDesc: "Instant PAN card issuance and e‑PAN services.",
    },
    {
      id: 5,
      title: "Utility Bill Payments",
      icon: "bill-icon.png",
      shortDesc:
        "Electricity, water, gas, DTH, and mobile recharge – all in one dashboard.",
    },
  ];

  return (
    <React.Fragment>
      <Header />
      <div className="services-page">
        <Helmet>
          <title>Our Services - Happy Pay Fintech</title>
          <meta
            name="description"
            content="Explore the wide range of services offered by Happy Pay Fintech – AEPS, DMT, CMS, PAN card, utility bill payments and more. Perfect for retailers and agents."
          />
        </Helmet>

        <div className="body-overlay"></div>

        <main>
          {/* ===== Hero / Breadcrumb ===== */}
          <section
            style={{
              background: "linear-gradient(145deg, #f9fafc 0%, #eef2f6 100%)",
              padding: "100px 0",
              textAlign: "center",
            }}
          >
            <div className="container">
              <div className="row">
                <div className="col-xxl-12">
                  <div className="breadcrumb__content p-relative z-index-1">
                    <h3
                      style={{
                        fontSize: "3rem",
                        fontWeight: "700",
                        color: "#1e293b",
                        marginBottom: "10px",
                        marginTop: "50px",
                      }}
                    >
                      Our Services
                    </h3>
                    <div style={{ color: "#64748b", fontSize: "1.1rem" }}>
                      <a
                        href="/"
                        style={{ color: "#f39c12", textDecoration: "none" }}
                      >
                        Home
                      </a>
                      <span style={{ margin: "0 10px" }}>•</span>
                      <span>About Us</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ===== Introduction ===== */}
          <section className="py-5">
            <div className="container">
              <div className="row">
                <div className="col-lg-8 mx-auto text-center">
                  <p className="lead text-muted">
                    At Happy Pay Fintech, we offer a comprehensive suite of
                    digital financial services designed to help retailers and
                    agents grow their business while serving their community.
                    Each service is built with security, speed, and simplicity
                    in mind.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ===== Services Cards (Icon + Title + Short Description only) ===== */}
          <section className="pb-5">
            <div className="container">
              <div className="row g-4">
                {services.map((service) => (
                  <div key={service.id} className="col-lg-4 col-md-6">
                    <div className="service-detail-card h-100 text-center">
                      <div className="service-icon-large mb-3">
                        <img
                          src={`/images/${service.icon}`}
                          alt={service.title}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = svgPlaceholder(
                              80,
                              80,
                              service.title.split(" ")[0],
                            );
                          }}
                          className="img-fluid"
                        />
                      </div>
                      <h3 className="h5 fw-bold">{service.title}</h3>
                      <p className="text-muted small mt-2">
                        {service.shortDesc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== Why Choose Us ===== */}
          <section className="py-5 bg-light" style={{ marginTop: "70px" }}>
            <div className="container">
              <h2 className="fw-bold text-center mb-5">
                Why Choose Happy Pay Fintech?
              </h2>
              <div className="row g-4">
                <div className="col-md-3 col-6">
                  <div className="text-center">
                    <div className="display-4 text-primary">🔒</div>
                    <h6 className="mt-2">Bank‑grade Security</h6>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="text-center">
                    <div className="display-4 text-primary">⚡</div>
                    <h6 className="mt-2">Instant Transactions</h6>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="text-center">
                    <div className="display-4 text-primary">📞</div>
                    <h6 className="mt-2">24×7 Support</h6>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="text-center">
                    <div className="display-4 text-primary">💰</div>
                    <h6 className="mt-2">Best Commissions</h6>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default Services;
